import { FormEvent, useEffect, useState } from 'react';
import { api } from '../lib/api';
import { classNames, formatCLP } from '../lib/format';
import type { Expense, FinanceSummary } from '../types';

const EXPENSE_CATEGORIES = ['general', 'arriendo', 'sueldos', 'marketing', 'logistica', 'insumos', 'servicios', 'impuestos', 'otros'];

function isoDaysAgo(days: number) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
}

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

export function FinancePanel({ advanced }: { advanced: boolean }) {
  const [from, setFrom] = useState(isoDaysAgo(30));
  const [to, setTo] = useState(todayIso());
  const [summary, setSummary] = useState<FinanceSummary | null>(null);
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [editing, setEditing] = useState<Partial<Expense>>({ description: '', category: 'general', amount: 0, expenseDate: todayIso(), notes: '' });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null);

  async function load() {
    setLoading(true);
    try {
      const [s, e] = await Promise.all([api.admin.finance(from, to), api.admin.expenses(from, to)]);
      setSummary(s);
      setExpenses(e);
    } catch (err) {
      setMessage({ tone: 'error', text: err instanceof Error ? err.message : 'Error' });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function saveExpense(event: FormEvent) {
    event.preventDefault();
    if (!editing.description || !editing.amount) {
      setMessage({ tone: 'error', text: 'Completa descripción y monto' });
      return;
    }
    try {
      await api.admin.saveExpense(editing);
      setEditing({ description: '', category: 'general', amount: 0, expenseDate: todayIso(), notes: '' });
      setMessage({ tone: 'ok', text: '✓ Egreso guardado.' });
      await load();
    } catch (err) {
      setMessage({ tone: 'error', text: err instanceof Error ? err.message : 'Error' });
    }
  }

  async function removeExpense(id: string, description: string) {
    if (!window.confirm(`¿Borrar el egreso "${description}"?`)) return;
    await api.admin.deleteExpense(id);
    if (editing.id === id) setEditing({ description: '', category: 'general', amount: 0, expenseDate: todayIso(), notes: '' });
    await load();
  }

  return (
    <div className="dashboard-stack">
      <section className="admin-card">
        <div className="admin-card-head">
          <div>
            <h2>Resumen financiero</h2>
            <p className="muted">Ingresos, egresos, comisiones MP, márgenes y utilidad neta del período.</p>
          </div>
          <div className="header-actions-row">
            <label className="inline-field">Desde<input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
            <label className="inline-field">Hasta<input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></label>
            <button type="button" className="primary-button compact" onClick={load} disabled={loading}>{loading ? '...' : 'Aplicar'}</button>
          </div>
        </div>

        {summary && (
          <>
            <div className="finance-grid">
              <article className="finance-card primary">
                <span>Ingresos netos</span>
                <strong>{formatCLP(summary.netRevenue)}</strong>
                <small>{summary.ordersCount} pedidos · ticket {formatCLP(summary.averageTicket)}</small>
              </article>
              <article className="finance-card">
                <span>Costo productos vendidos</span>
                <strong>-{formatCLP(summary.cogs)}</strong>
                <small>Suma de costos por producto vendido</small>
              </article>
              <article className="finance-card">
                <span>Comisión MercadoPago (estimada)</span>
                <strong>-{formatCLP(summary.estimatedMpCommission)}</strong>
                <small>Tasa configurable en Configuración</small>
              </article>
              <article className="finance-card success">
                <span>Margen bruto</span>
                <strong>{formatCLP(summary.grossMargin)}</strong>
                <small>Después de costo + comisión MP</small>
              </article>
              <article className="finance-card">
                <span>Egresos</span>
                <strong>-{formatCLP(summary.expenses)}</strong>
                <small>Gastos operacionales del período</small>
              </article>
              <article className={classNames('finance-card', summary.netProfit >= 0 ? 'success' : 'danger')}>
                <span>Utilidad neta</span>
                <strong>{formatCLP(summary.netProfit)}</strong>
                <small>Margen − egresos</small>
              </article>
            </div>

            <div className="finance-breakdown">
              <h3>Desglose</h3>
              <ul>
                <li><span>Ventas brutas (subtotal antes de descuentos)</span><strong>{formatCLP(summary.grossRevenue)}</strong></li>
                <li><span>− Descuentos / cupones aplicados</span><strong>-{formatCLP(summary.discounts)}</strong></li>
                <li><span>− Giftcards canjeadas</span><strong>-{formatCLP(summary.giftcardsRedeemed)}</strong></li>
                <li><span>+ Despacho cobrado</span><strong>+{formatCLP(summary.shippingCharged)}</strong></li>
                <li><span>− Pedidos cancelados/reembolsados</span><strong>-{formatCLP(summary.refunds)}</strong></li>
                <li className="finance-li-total"><span>= Ingresos netos</span><strong>{formatCLP(summary.netRevenue)}</strong></li>
              </ul>
            </div>

            <div className="admin-grid two-col">
              <article className="admin-card">
                <h3>Por canal de venta</h3>
                <ul className="top-products">
                  {summary.byChannel.map((c) => (
                    <li key={c.channel}>
                      <div className="top-meta">
                        <strong>{c.channel === 'manual' ? 'Manual / presencial' : c.channel === 'online' ? 'Online' : c.channel}</strong>
                        <small>{c.orders} pedidos</small>
                      </div>
                      <span className="top-revenue">{formatCLP(c.revenue)}</span>
                    </li>
                  ))}
                </ul>
              </article>
              <article className="admin-card">
                <h3>Giftcards</h3>
                <dl className="settings-list">
                  <div><dt>Vendidas en período</dt><dd>{formatCLP(summary.giftcardsSold)}</dd></div>
                  <div><dt>Saldo pendiente (todas activas)</dt><dd>{formatCLP(summary.giftcardsOutstanding)}</dd></div>
                  <div><dt>Canjeadas en período</dt><dd>{formatCLP(summary.giftcardsRedeemed)}</dd></div>
                </dl>
                <p className="muted small">El saldo pendiente es un pasivo: el dinero ya entró pero todavía no se canjea.</p>
              </article>
            </div>
          </>
        )}
      </section>

      <section className="admin-card">
        <div className="admin-card-head">
          <h2>{editing.id ? 'Editar egreso' : 'Registrar egreso'}</h2>
          {editing.id && (
            <button type="button" className="ghost-button compact" onClick={() => setEditing({ description: '', category: 'general', amount: 0, expenseDate: todayIso(), notes: '' })}>
              Cancelar edición
            </button>
          )}
        </div>
        <form onSubmit={saveExpense} className="form-grid">
          <label className="span-2">
            Descripción
            <input value={editing.description || ''} onChange={(e) => setEditing({ ...editing, description: e.target.value })} placeholder="Ej: Arriendo bodega abril" required />
          </label>
          <label>
            Categoría
            <select value={editing.category || 'general'} onChange={(e) => setEditing({ ...editing, category: e.target.value })}>
              {EXPENSE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </label>
          <label>
            Monto (CLP)
            <input type="number" min={0} value={editing.amount || 0} onChange={(e) => setEditing({ ...editing, amount: Number(e.target.value) })} required />
          </label>
          <label>
            Fecha
            <input type="date" value={editing.expenseDate || todayIso()} onChange={(e) => setEditing({ ...editing, expenseDate: e.target.value })} required />
          </label>
          {advanced && (
            <label className="span-2">
              Notas
              <textarea rows={2} value={editing.notes || ''} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} />
            </label>
          )}
          <div className="span-2">
            <button className="primary-button compact">{editing.id ? 'Actualizar' : 'Agregar egreso'}</button>
          </div>
        </form>
      </section>

      <section className="admin-card">
        <h2>Egresos del período</h2>
        {expenses.length === 0 ? (
          <div className="empty-state subtle"><p>Sin egresos en este rango.</p></div>
        ) : (
          <div className="expenses-table">
            <div className="expenses-row expenses-head">
              <span>Fecha</span><span>Descripción</span><span>Categoría</span><span>Monto</span><span></span>
            </div>
            {expenses.map((e) => (
              <div className="expenses-row" key={e.id}>
                <span>{new Date(e.expenseDate + 'T12:00:00Z').toLocaleDateString('es-CL')}</span>
                <span><strong>{e.description}</strong>{e.notes && <small> · {e.notes}</small>}</span>
                <span className="expense-cat">{e.category}</span>
                <strong>{formatCLP(e.amount)}</strong>
                <div className="giftcard-actions">
                  <button type="button" className="ghost-button compact" onClick={() => setEditing(e)}>Editar</button>
                  <button type="button" className="ghost-button compact danger" onClick={() => removeExpense(e.id, e.description)}>×</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {message && <p className={classNames('form-message', `tone-${message.tone}`)}>{message.text}</p>}
    </div>
  );
}
